import type { Api_Feed_Video, Api_LiveStream, Api_Video } from '@core/api'
import { Enum_LiveStream_Status } from '@core/api'
import type { FeedSourceConfig } from '@core/feed'
import isBoolean from '@core/lodash/isBoolean'
import isNumber from '@core/lodash/isNumber'
import isString from '@core/lodash/isString'
import { logError } from '@core/logging'
import type { IAnyWidgetParams } from '@core/types'
import { parseQueryParams } from '@core/window'
import type {
  AnyWidgetAppContext,
  IWidgetAppContext,
} from '@embed/hooks/useEmbedAppContext'
import type { IPWAState } from '@microsites/store'

import type { AppContextType } from './components/helpers'

export interface ISerializedAppContext {
  params: Partial<IAnyWidgetParams>
  appContextType: AppContextType
  username: AnyWidgetAppContext['username']
  guestId: AnyWidgetAppContext['guestId']
  embedFeedConfig: FeedSourceConfig
  trackingData: AnyWidgetAppContext['trackingData']
  trackingDataPixel: AnyWidgetAppContext['trackingDataPixel']
  channel?: {
    business_id?: string
  }
}

/**
 *
 */
export function getSerializableAppContext(
  appContext: IWidgetAppContext<IAnyWidgetParams>,
  params: IAnyWidgetParams,
  video?: Api_Feed_Video | null,
): ISerializedAppContext {
  const {
    appContextType,
    username,
    guestId,
    embedFeedConfig,
    trackingData,
    trackingDataPixel,
  } = appContext
  return {
    params: Object.keys(params).reduce(
      (map, key) => {
        if (
          isNumber(params[key as keyof IAnyWidgetParams]) ||
          isString(params[key as keyof IAnyWidgetParams]) ||
          isBoolean(params[key as keyof IAnyWidgetParams])
        ) {
          map[key] = params[key as keyof IAnyWidgetParams]
        }
        return map
      },
      {} as Record<string, any>,
    ),
    appContextType,
    username,
    guestId,
    embedFeedConfig: { ...embedFeedConfig, firstVideoId: video?.id },
    trackingData,
    trackingDataPixel,
  }
}

/**
 *
 */
export const createLinkToEmbedVideoPage = (
  appContext: IWidgetAppContext<IAnyWidgetParams>,
  params: IAnyWidgetParams,
  video: Api_Feed_Video | null,
): string => {
  const url = new URL(
    `${params.microsites_host || 'https://fw.tv'}/embed/videos/${video?.id}`,
  )

  url.searchParams.append(
    'app_context',
    JSON.stringify(getSerializableAppContext(appContext, params, video)),
  )
  return url.href
}

/**
 *
 */
export const abbreviateNumber = (number: number): string => {
  const tier = (Math.log10(number) / 3) | 0

  if (tier === 0) {
    return String(number)
  }

  const suffix = ['', 'k', 'M', 'G', 'T', 'P', 'E'][tier]
  const scale = Math.pow(10, tier * 3)
  const scaled = number / scale
  const parts = String(scaled).match(/^([0-9]+)\.?([0-9]+)?$/)

  if (!parts) {
    return ''
  }

  return (
    `${parts[1]}.${parts[2] ? parts[2][0] : '0'}`.replace(/\.0$/, '') + suffix
  )
}

/**
 *
 */
export const removeEmojis = (text: string) => {
  // https://www.regextester.com/106421
  // the following code removes the following characters
  // 0x00A9(copy right symbol),
  // 0x00AE(register symbol),
  // 0x1F000 - 0x1F3FF, 0x1F400 - 0x1F7FF, 0x1F800 - 0x1FBFF (various emoji, symobols and pictographs)
  return typeof text === 'string'
    ? text
        .replace(
          /(\u00a9|\u00ae|[\u2000-\u2BFF]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g,
          '',
        )
        .trim()
    : ''
}

/**
 *
 */
export const detectWordpress = () => {
  const meta =
    typeof window !== 'undefined' &&
    document.querySelector("meta[name='generator']")
  return (
    meta &&
    meta.hasAttribute('content') &&
    meta.getAttribute('content')!.toLowerCase().startsWith('wordpress')
  )
}

/**
 *
 */
export const isTouchDevice = (): boolean =>
  'ontouchstart' in window || navigator.maxTouchPoints > 0

/**
 *
 */
export const extractStartTimeFromQuerystring = (location: Location): number => {
  const { href } = location || {}
  const [, query] = href ? href.split('?') : []
  const { start_time } = parseQueryParams(query)
  if (start_time) {
    return Number(start_time) || 0
  }
  return 0
}

/**
 *
 */
export const extractPageUrlFromQuerystring = (location: Location) => {
  const { href } = location || {}
  const [, query] = href ? href.split('?') : []
  let { page_url } = parseQueryParams(query)
  if (page_url) {
    // if the href contains page_url, return its page_url
    // remove query if page_url still contains query
    if (String(page_url).includes('?')) {
      page_url = String(page_url).split('?')[0]
    }
    return page_url
  }
  return null
}

/**
 *
 */
export const extractPageUrlPwa = (params: IPWAState['params']) => {
  const { location } = params || {}
  const { href } = location || {}
  return href || null
}

/**
 *
 */
export const isValidEmail = (email: string) => {
  if (!email) {
    return false
  }
  return !!email.match(/(.+)@(.+)\.(.+)/)
}

/**
 *
 */
export const stringHasContent = (str: string) => str.search(/[^ ]+/) !== -1

/**
 * Last status observed for a livestream through the widgets lite WebSocket
 * (`updateVideoFromLiveStream`), keyed by video id (`Api_LiveStream` has no id
 * of its own). The widgets store's `liveStreams` object is never populated, so
 * this is the only live baseline available to the feed check; a stale cached
 * `video.live_stream_status` from the embed v2 feed never writes to it. The
 * Player3 store, by contrast, has an authoritative `liveStreams` object and
 * uses that as its baseline instead of this map.
 */
const lastLiveStreamStatusById = new Map<string, Enum_LiveStream_Status>()

/**
 * Reports a pre-recorded (v2l) stream that reached `idle` after being seen
 * `active`, which is an invalid backend state, to Sentry.
 */
const reportPreRecordedActiveToIdle = (
  message: string,
  video: Api_Video,
  extra: Record<string, unknown>,
): void => {
  logError(new Error(message), {
    captureContext: {
      tags: { live_stream_status_transition: 'active_to_idle' },
      extra: {
        video_id: video.id,
        live_stream_id: video.live_stream_id,
        live_stream_source: video.live_stream_source,
        base_video_id: video.live_stream_base_video_id,
        ...extra,
      },
    },
  })
}

/**
 * Reports the invalid `active` -> `idle` transition for a pre-recorded (v2l)
 * stream to Sentry. The `previousStatus` must come from an authoritative live
 * source (e.g. the Player3 store `liveStreams` object), never from a video
 * object that may carry a stale status from a cached embed v2 response.
 */
export const detectPreRecordedActiveToIdle = (
  video: Api_Video | null | undefined,
  previousStatus: Enum_LiveStream_Status | undefined,
  nextStatus: Enum_LiveStream_Status | undefined,
  source: string,
  liveStreamDetails?: Partial<Api_LiveStream>,
): void => {
  if (
    video?.live_stream_source !== 'prerecorded' ||
    previousStatus !== Enum_LiveStream_Status.ACTIVE ||
    nextStatus !== Enum_LiveStream_Status.IDLE
  ) {
    return
  }

  reportPreRecordedActiveToIdle(
    'Pre-recorded live stream transitioned from active to idle',
    video,
    {
      source,
      provider: liveStreamDetails?.provider,
      started_at: liveStreamDetails?.started_at,
      ended_at: liveStreamDetails?.ended_at,
      scheduled_at: liveStreamDetails?.scheduled_at,
    },
  )
}

/**
 * Records a live status seen through the widgets lite WebSocket into
 * `lastLiveStreamStatusById` and reports the invalid `active` -> `idle`
 * transition if it happens live. Used by the widgets store, which has no
 * populated `liveStreams` object to compare against.
 */
export const recordAndDetectLiveStreamStatus = (
  video: Api_Video | null | undefined,
  liveStream: Partial<Api_LiveStream>,
): void => {
  if (
    !video ||
    video.live_stream_source !== 'prerecorded' ||
    !liveStream.status
  ) {
    return
  }

  detectPreRecordedActiveToIdle(
    video,
    lastLiveStreamStatusById.get(video.id),
    liveStream.status,
    'updateVideoFromLiveStream',
    liveStream,
  )

  lastLiveStreamStatusById.set(video.id, liveStream.status)
}

/**
 * Detects a pre-recorded (v2l) video coming from an embed v2 feed fetch that
 * carries an outdated `idle` status while the widgets lite WebSocket already
 * observed the stream `active`. The (potentially CDN-cached) feed response is
 * never trusted to update the live baseline, so it only reports and never
 * mutates it.
 */
export const detectOutdatedFeedLiveStreamStatus = (video: Api_Video): void => {
  if (
    video.live_stream_source !== 'prerecorded' ||
    video.live_stream_status !== Enum_LiveStream_Status.IDLE
  ) {
    return
  }

  if (
    lastLiveStreamStatusById.get(video.id) === Enum_LiveStream_Status.ACTIVE
  ) {
    reportPreRecordedActiveToIdle(
      'Embed v2 feed returned outdated idle status for a pre-recorded live stream',
      video,
      { source: 'embedV2Feed' },
    )
  }
}

/**
 *
 */
export const updateVideoLiveStreamParams = (
  video: Api_Video | null,
  liveStream: Partial<Api_LiveStream> | null,
  forceReplay = false,
): Partial<Api_Video> | null => {
  // Websocket payloads carry the live stream detail without
  // `replay_filtered_url`, so an absent field means "unchanged" rather than
  // "cleared": the filtered replay resolved by `/detail` has to survive the
  // `complete_live_stream` update that moves the stream into replay. An
  // explicit `null` still clears it.
  const keyHasChanged = (key: keyof Api_LiveStream): boolean => {
    if (key === 'replay_filtered_url' && liveStream?.[key] === undefined) {
      return false
    }

    return (
      video?.[`live_stream_${key}` as keyof Api_Video] !== liveStream?.[key]
    )
  }

  const keysToCheck: (keyof Api_LiveStream)[] = [
    'announcement',
    'chat_enabled',
    'chat_in_replay_enabled',
    'ended_at',
    'hearts_count_enabled',
    'playback_url',
    'provider',
    'replay_enabled',
    'replay_filtered_url',
    'replay_url',
    'scheduled_at',
    'started_at',
    'status',
    'viewers_count_enabled',
    'business_privacy_policy_url',
    'subtitles',
  ]

  if (
    video &&
    liveStream &&
    video.id === liveStream.video_id &&
    keysToCheck.some((key) => keyHasChanged(key))
  ) {
    const updatedVideo = Object.fromEntries(
      keysToCheck
        .filter((key) => keyHasChanged(key))
        .map((key) => [`live_stream_${key}`, liveStream[key]]),
    )

    if (forceReplay) {
      updatedVideo.live_stream_replay_enabled = true
    }
    return updatedVideo
  }
  return null
}

export const TEST_ONLY = {
  resetLiveStreamStatusTracking: (): void => lastLiveStreamStatusById.clear(),
}
